import React, { useState } from 'react';
import { Building2, Menu, SlidersHorizontal } from 'lucide-react';
import { useNavigate, NavLink } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useFeeCategories } from '@/contexts/FeeCategoryContext';

const CategoryFilter: React.FC = () => {
  const { categories, enabledCategories, toggleCategory } = useFeeCategories();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5 shrink-0">
          <SlidersHorizontal className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Avgifter</span>
          <span className="text-xs text-warmGray">({enabledCategories.length}/{categories.length})</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="z-[1000]">
        <DropdownMenuLabel>Vis avgifter</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {categories.map((category) => (
          <DropdownMenuCheckboxItem
            key={category.id}
            checked={enabledCategories.includes(category.id)}
            onCheckedChange={() => toggleCategory(category.id)}
            onSelect={(e) => e.preventDefault()}
          >
            {category.name}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const Header: React.FC = () => {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `relative py-2 px-3 text-sm font-medium rounded-lg transition-all duration-200 block ${
      isActive
        ? 'text-nordicBlue bg-nordicBlue/5'
        : 'text-deepCharcoal/80 hover:text-nordicBlue hover:bg-nordicBlue/5'
    }`;

  const closeMobile = () => setMobileOpen(false);

  const navLinks = (
    <>
      <NavLink to="/" className={navLinkClass} onClick={closeMobile}>
        Hjem
      </NavLink>
      <NavLink to="/sammenlign" className={navLinkClass} onClick={closeMobile}>
        Sammenlign
      </NavLink>
      <NavLink to="/kart" className={navLinkClass} onClick={closeMobile}>
        Kart
      </NavLink>
      <NavLink to="/del" className={navLinkClass} onClick={closeMobile}>
        Del
      </NavLink>
      <NavLink to="/om" className={navLinkClass} onClick={closeMobile}>
        Om
      </NavLink>
    </>
  );

  const desktopNav = (
    <>
      <NavLink to="/" className={navLinkClass}>
        Hjem
      </NavLink>
      <span className="w-px h-4 bg-gray-200 mx-2" aria-hidden />
      <NavLink to="/sammenlign" className={navLinkClass}>
        Sammenlign
      </NavLink>
      <span className="w-px h-4 bg-gray-200 mx-2" aria-hidden />
      <NavLink to="/kart" className={navLinkClass}>
        Kart
      </NavLink>
      <span className="w-px h-4 bg-gray-200 mx-2" aria-hidden />
      <NavLink to="/del" className={navLinkClass}>
        Del
      </NavLink>
      <span className="w-px h-4 bg-gray-200 mx-2" aria-hidden />
      <NavLink to="/om" className={navLinkClass}>
        Om
      </NavLink>
    </>
  );

  return (
    <header className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm shadow-sm border-b border-gray-100">
      <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-nordicBlue/30 to-transparent" />

      <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-4 flex justify-between items-center gap-2">
        <div
          className="flex items-center gap-2 sm:gap-3 cursor-pointer group min-w-0 shrink"
          onClick={() => navigate('/')}
        >
          <div className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-nordicBlue/15 to-nordicBlue/5 group-hover:from-nordicBlue/25 group-hover:to-nordicBlue/10 transition-all duration-200 shrink-0">
            <Building2 className="text-nordicBlue h-4 w-4 sm:h-5 sm:w-5" strokeWidth={2} />
          </div>
          <div className="flex flex-col min-w-0">
            <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
              <h1 className="text-base sm:text-lg font-bold text-deepCharcoal tracking-wide truncate">
                Kommunale avgifter
              </h1>
              <span className="px-1.5 sm:px-2 py-0.5 text-[10px] font-semibold bg-nordicBlue/10 text-nordicBlue rounded-md border border-nordicBlue/20 shrink-0">
                BETA
              </span>
            </div>
            <p className="text-xs text-warmGray hidden sm:block">Sammenlign vann, avløp og renovasjon</p>
          </div>
        </div>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1">
          {desktopNav}
        </nav>

        <div className="flex items-center gap-2 shrink-0">
          <CategoryFilter />

          {/* Mobile menu */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden shrink-0">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Åpne meny</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] sm:w-[320px] z-[1000]" overlayClassName="z-[1000]">
              <SheetHeader>
                <SheetTitle>Meny</SheetTitle>
              </SheetHeader>
              <nav className="flex flex-col gap-1 mt-6">
                {navLinks}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
